import { cur } from '@/lib/currency';
import { useEffect, useState } from 'react';
import { useBranchStore } from '@/stores';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Undo2, Download, Loader2, AlertCircle, User as UserIcon, Building2, Calendar } from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { localToday } from '@/lib/dateRange';
import { BranchMultiSelect } from '@/components/BranchMultiSelect';
import { DateQuickFilter } from '@/components/DateQuickFilter';
import { PageSizeSelector } from '@/components/common/PageSizeSelector';

export default function ReversalsReportPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [suppliers, setSuppliers] = useState([]);
  const [branches, setBranches] = useState([]);
  const [users, setUsers] = useState([]);
  const [filters, setFilters] = useState({ start_date: '', end_date: '', supplier_id: '', branch_id: '', branch_ids: '', user_id: '' });
  const posBranch = useBranchStore((s) => s.activeBranchId);
  const [branchSel, setBranchSel] = useState(posBranch ? [posBranch] : []);
  const [revPageSize, setRevPageSize] = useState(50);
  const [revPage, setRevPage] = useState(1);

  const fmt = (v) => `${cur()} ${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const fmt0 = (v) => `${cur()} ${Number(v || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

  const fetchReport = async (f = filters) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      Object.entries(f).forEach(([k, v]) => { if (v) params.append(k, v); });
      const qs = params.toString();
      const res = await api.get(`/supplier-payments/reversals${qs ? `?${qs}` : ''}`);
      setData(res.data);
      setRevPage(1);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load report');
    } finally { setLoading(false); }
  };

  useEffect(() => {
    fetchReport();
    api.get('/suppliers').then(r => setSuppliers(r.data || [])).catch(() => {});
    api.get('/branches').then(r => setBranches(r.data || [])).catch(() => {});
    api.get('/users').then(r => setUsers(r.data || [])).catch(() => setUsers([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // iter963 — Follow the shared POS/Stock active branch (branchStore).
  useEffect(() => {
    const arr = posBranch ? [posBranch] : [];
    setBranchSel(arr);
    setFilters((prev) => {
      const nf = { ...prev, branch_ids: arr.join(','), branch_id: '' };
      fetchReport(nf);
      return nf;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [posBranch]);

  const applyFilters = () => fetchReport(filters);
  const resetFilters = () => {
    const cleared = { start_date: '', end_date: '', supplier_id: '', branch_id: '', branch_ids: '', user_id: '' };
    setFilters(cleared);
    setBranchSel([]);
    fetchReport(cleared);
  };

  const exportCsv = () => {
    if (!data) return;
    const rows = ['Reversed At,Reversed By,Supplier,Branch,Amount,Payment Mode,Paid On,Reason,Notes'];
    (data.reversals || []).forEach(r => rows.push([
      r.reversed_at?.slice(0, 19) || '',
      `"${r.reversed_by_name || ''}"`,
      `"${r.supplier_name || ''}"`,
      `"${r.branch_name || ''}"`,
      r.amount,
      r.payment_mode || '',
      r.paid_on?.slice(0, 10) || '',
      `"${(r.reverse_reason || '').replace(/"/g, '""')}"`,
      `"${(r.notes || '').replace(/"/g, '""')}"`,
    ].join(',')));
    const blob = new Blob([rows.join('\n')], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `reversals_report_${localToday()}.csv`;
    a.click();
    toast.success('Exported');
  };

  if (loading) return <DashboardLayout><div className="flex items-center justify-center h-64"><Loader2 className="animate-spin" /></div></DashboardLayout>;

  const s = data?.summary || {};

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4 flex-wrap">
          <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-warning to-error flex items-center justify-center shadow-lg shadow-warning/30">
            <Undo2 size={24} className="text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <h1 className="text-2xl sm:text-3xl font-bold font-outfit tracking-tight text-foreground" data-testid="reversals-title">Reversals Report</h1>
            <p className="text-sm text-muted-foreground mt-0.5">Audit trail of reversed supplier payments — who, what, when & why</p>
          </div>
          <Button variant="outline" className="rounded-xl" onClick={exportCsv} disabled={!data?.reversals?.length} data-testid="reversals-export-btn">
            <Download size={14} className="mr-1" /> Export CSV
          </Button>
        </div>

        {/* Filters */}
        <Card className="border-border">
          <CardContent className="p-4">
            <DateQuickFilter
              className="mb-3"
              onFilterChange={(range) => { const nf = { ...filters, start_date: range?.start || '', end_date: range?.end || '' }; setFilters(nf); fetchReport(nf); }}
            />
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 items-end">
              <div>
                <Label className="text-xs text-muted-foreground">User</Label>
                <Select value={filters.user_id || 'all'} onValueChange={(v) => setFilters({ ...filters, user_id: v === 'all' ? '' : v })}>
                  <SelectTrigger className="h-9" data-testid="filter-user"><SelectValue placeholder="All" /></SelectTrigger>
                  <SelectContent><SelectItem value="all">All users</SelectItem>{users.map(u => <SelectItem key={u.id} value={u.id}>{u.full_name || u.email}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Supplier</Label>
                <Select value={filters.supplier_id || 'all'} onValueChange={(v) => setFilters({ ...filters, supplier_id: v === 'all' ? '' : v })}>
                  <SelectTrigger className="h-9" data-testid="filter-supplier"><SelectValue placeholder="All" /></SelectTrigger>
                  <SelectContent><SelectItem value="all">All suppliers</SelectItem>{suppliers.map(x => <SelectItem key={x.id} value={x.id}>{x.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Branch</Label>
                <BranchMultiSelect
                  value={branchSel}
                  onChange={(arr) => { setBranchSel(arr); const nf = { ...filters, branch_ids: arr.join(','), branch_id: '' }; setFilters(nf); fetchReport(nf); }}
                  branches={branches}
                  width="w-full"
                  testid="reversals-branch-multiselect"
                />
              </div>
              <div className="flex gap-2">
                <Button onClick={applyFilters} className="flex-1 h-9 rounded-xl" data-testid="apply-filters-btn">Apply</Button>
                <Button variant="outline" onClick={resetFilters} className="h-9 rounded-xl">Reset</Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Summary pills */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="p-3 bg-warning/10 rounded-xl border border-warning/30">
            <div className="text-[10px] uppercase tracking-wider text-warning font-semibold">Total Reversals</div>
            <div className="text-2xl font-bold font-outfit text-warning mt-0.5" data-testid="summary-total-reversals">{s.total_reversals || 0}</div>
          </div>
          <div className="p-3 bg-error/10 rounded-xl border border-error/30">
            <div className="text-[10px] uppercase tracking-wider text-error font-semibold">Total Amount Reversed</div>
            <div className="text-2xl font-bold font-outfit text-error mt-0.5" data-testid="summary-total-amount">{fmt0(s.total_amount)}</div>
          </div>
          <div className="p-3 bg-info/10 rounded-xl border border-info/30">
            <div className="text-[10px] uppercase tracking-wider text-info font-semibold">Unique Users</div>
            <div className="text-2xl font-bold font-outfit text-info mt-0.5">{s.unique_users || 0}</div>
          </div>
          <div className="p-3 bg-success/10 rounded-xl border border-success/30">
            <div className="text-[10px] uppercase tracking-wider text-success font-semibold">Suppliers Affected</div>
            <div className="text-2xl font-bold font-outfit text-success mt-0.5">{s.unique_suppliers || 0}</div>
          </div>
        </div>

        {/* Per-User + Per-Supplier + Per-Month */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <Card className="border-border">
            <CardHeader className="pb-2"><CardTitle className="text-sm font-outfit flex items-center gap-2"><UserIcon size={14} /> By user</CardTitle></CardHeader>
            <CardContent className="pt-0">
              {(data?.by_user || []).length === 0 ? <p className="text-xs text-muted-foreground py-4 text-center">No reversals</p> : (
                <div className="space-y-1.5 max-h-64 overflow-y-auto">
                  {data.by_user.map(r => (
                    <button
                      key={r.user_id}
                      type="button"
                      onClick={() => { const nf = { ...filters, user_id: r.user_id }; setFilters(nf); fetchReport(nf); }}
                      className={`w-full text-left flex justify-between items-center p-2 border-b border-border/60 text-xs hover:bg-primary/5 transition-colors ${filters.user_id === r.user_id ? 'bg-primary/10 border-l-2 border-l-primary -ml-0.5 pl-[7px]' : ''}`}
                      data-testid={`by-user-row-${r.user_id}`}
                    >
                      <span className="font-medium truncate">{r.user_name}</span>
                      <div className="flex gap-2 items-center"><Badge variant="outline" className="text-[10px]">{r.count}x</Badge><span className="font-bold text-error">{fmt0(r.total_amount)}</span></div>
                    </button>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
          <Card className="border-border">
            <CardHeader className="pb-2"><CardTitle className="text-sm font-outfit flex items-center gap-2"><Building2 size={14} /> By supplier</CardTitle></CardHeader>
            <CardContent className="pt-0">
              {(data?.by_supplier || []).length === 0 ? <p className="text-xs text-muted-foreground py-4 text-center">No reversals</p> : (
                <div className="space-y-1.5 max-h-64 overflow-y-auto">
                  {data.by_supplier.map(r => (
                    <button
                      key={r.supplier_id}
                      type="button"
                      onClick={() => { const nf = { ...filters, supplier_id: r.supplier_id }; setFilters(nf); fetchReport(nf); }}
                      className={`w-full text-left flex justify-between items-center p-2 border-b border-border/60 text-xs hover:bg-primary/5 transition-colors ${filters.supplier_id === r.supplier_id ? 'bg-primary/10 border-l-2 border-l-primary -ml-0.5 pl-[7px]' : r.count >= 3 ? 'bg-warning/5' : ''}`}
                      data-testid={`by-supplier-row-${r.supplier_id}`}
                    >
                      <span className="font-medium truncate flex items-center gap-1">
                        {r.count >= 3 && <AlertCircle size={12} className="text-warning" />}
                        {r.supplier_name}
                      </span>
                      <div className="flex gap-2 items-center"><Badge variant="outline" className="text-[10px]">{r.count}x</Badge><span className="font-bold text-error">{fmt0(r.total_amount)}</span></div>
                    </button>
                  ))}
                </div>
              )}
              {(data?.by_supplier || []).some(s => s.count >= 3) && (
                <div className="mt-2 p-2 bg-warning/10 rounded text-[10px] text-warning/90 border border-warning/30">
                  <AlertCircle size={10} className="inline mr-1" /> Suppliers with 3+ reversals highlighted — pattern worth investigating
                </div>
              )}
            </CardContent>
          </Card>
          <Card className="border-border">
            <CardHeader className="pb-2"><CardTitle className="text-sm font-outfit flex items-center gap-2"><Calendar size={14} /> By month</CardTitle></CardHeader>
            <CardContent className="pt-0">
              {(data?.by_month || []).length === 0 ? <p className="text-xs text-muted-foreground py-4 text-center">No reversals</p> : (
                <div className="space-y-1.5 max-h-64 overflow-y-auto">
                  {data.by_month.map(r => (
                    <button
                      key={r.month}
                      type="button"
                      onClick={() => {
                        const [y, m] = r.month.split('-');
                        if (!y || !m) return;
                        const last = new Date(Number(y), Number(m), 0).getDate();
                        const nf = { ...filters, start_date: `${r.month}-01`, end_date: `${r.month}-${String(last).padStart(2, '0')}` };
                        setFilters(nf); fetchReport(nf);
                      }}
                      className={`w-full text-left flex justify-between items-center p-2 border-b border-border/60 text-xs hover:bg-primary/5 transition-colors ${filters.start_date?.startsWith(r.month) ? 'bg-primary/10 border-l-2 border-l-primary -ml-0.5 pl-[7px]' : ''}`}
                    >
                      <span className="font-medium">{r.month}</span>
                      <div className="flex gap-2 items-center"><Badge variant="outline" className="text-[10px]">{r.count}x</Badge><span className="font-bold text-error">{fmt0(r.total_amount)}</span></div>
                    </button>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Detailed table */}
        <Card className="border-border">
          <CardHeader className="pb-3 flex-row items-center justify-between">
            <CardTitle className="text-base font-outfit">Detailed reversal log</CardTitle>
            <div className="flex items-center gap-2">
              <PageSizeSelector value={revPageSize} onChange={(n) => { setRevPageSize(n); setRevPage(1); }} testid="reversals-page-size" />
              {(() => {
                const totalPages = Math.max(1, Math.ceil((data?.reversals?.length || 0) / revPageSize));
                return totalPages > 1 && (
                  <div className="flex items-center gap-1" data-testid="reversals-pagination">
                    <Button size="sm" variant="outline" disabled={revPage <= 1} onClick={() => setRevPage(p => p - 1)}>Prev</Button>
                    <span className="text-[10px] text-muted-foreground">Page {revPage} / {totalPages}</span>
                    <Button size="sm" variant="outline" disabled={revPage >= totalPages} onClick={() => setRevPage(p => p + 1)}>Next</Button>
                  </div>
                );
              })()}
            </div>
          </CardHeader>
          <CardContent className="pt-0">
            {(data?.reversals || []).length === 0 ? (
              <div className="text-center py-10 text-muted-foreground">
                <Undo2 size={36} className="mx-auto mb-2 opacity-30" />
                <p className="text-sm">No reversed payments for this filter</p>
                <p className="text-xs mt-1">That's usually a good thing — clean payment history.</p>
              </div>
            ) : (
              <div className="overflow-x-auto border rounded-xl">
                <table className="w-full">
                  <thead className="bg-muted/40">
                    <tr className="border-b">
                      <th className="text-left p-2 text-xs font-medium">Reversed</th>
                      <th className="text-left p-2 text-xs font-medium">By user</th>
                      <th className="text-left p-2 text-xs font-medium">Supplier</th>
                      <th className="text-left p-2 text-xs font-medium">Branch</th>
                      <th className="text-right p-2 text-xs font-medium">Amount</th>
                      <th className="text-left p-2 text-xs font-medium">Mode</th>
                      <th className="text-left p-2 text-xs font-medium">Paid on</th>
                      <th className="text-left p-2 text-xs font-medium">Reason</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.reversals.slice((revPage - 1) * revPageSize, revPage * revPageSize).map((r) => (
                      <tr key={r.payment_id} className="border-b hover:bg-muted/30" data-testid={`reversal-row-${r.payment_id}`}>
                        <td className="p-2 text-xs whitespace-nowrap">{r.reversed_at ? new Date(r.reversed_at).toLocaleString() : '—'}</td>
                        <td className="p-2 text-xs font-medium">{r.reversed_by_name}</td>
                        <td className="p-2 text-xs">
                          {r.source === 'pos_void' && <Badge className="mr-1 text-[9px] bg-purple-100 text-purple-700 border-purple-300" variant="outline" data-testid="src-pos-void">POS Void</Badge>}
                          {r.source === 'pos_refund' && <Badge className="mr-1 text-[9px] bg-rose-100 text-rose-700 border-rose-300" variant="outline" data-testid="src-pos-refund">POS Refund</Badge>}
                          {r.supplier_name}
                        </td>
                        <td className="p-2 text-xs text-muted-foreground">{r.branch_name}</td>
                        <td className="p-2 text-xs text-right font-bold text-error">{fmt(r.amount)}</td>
                        <td className="p-2 text-xs"><Badge variant="outline" className="text-[10px]">{r.payment_mode}</Badge></td>
                        <td className="p-2 text-xs text-muted-foreground">{r.paid_on?.slice(0, 10) || '—'}</td>
                        <td className="p-2 text-xs max-w-xs truncate text-foreground/70" title={r.reverse_reason}>{r.reverse_reason || <span className="text-muted-foreground/60 italic">no reason</span>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
